/**
 * Fonte ÚNICA de cores de status do HUB — ver docs/ui-guidelines.md §4.
 * Regra: NENHUMA cor de status inline nova; todo badge importa daqui.
 * Modelo visual: fiscal-docs (cor no texto/número, fundo translúcido calmo).
 */

export type StatusTone =
  | 'open'      // aberto/ativo/em dia (verde)
  | 'success'   // ganho/autorizada/concluído (esmeralda)
  | 'progress'  // em andamento/participando (azul)
  | 'live'      // acontecendo AGORA — pulsa (âmbar)
  | 'warning'   // atenção/pendente/suspensa/vencendo (âmbar)
  | 'danger'    // erro/vencido/revogada/perdido (vermelho)
  | 'neutral'   // terminal neutro/desligado (slate)
  | 'info'      // informativo/categoria (roxo)
  | 'group'     // grupo econômico: rateio, compartilhado, matriz/coligada (violeta, decisão do Heitor 03/10/2026)
  | 'future'    // vigência FUTURA de item (céu): ainda não vale, fora do subtotal de hoje
  | 'regua'     // rateio: "o que decide quanto cada um paga" (a base). Tom PRÓPRIO, não o `future` emprestado:
                // mudar a cor de vigência não pode mudar a régua do rateio sem ninguém notar (06/10/2026).
                // Ciano, matiz próprio (regra da casa: um matiz por papel). A prévia aprovou o SIGNIFICADO, régua fria
                // ao lado do bolo violeta, e o céu dela era o `future` emprestado (decisão do Maestro, 06/10/2026).
  | 'ps';       // P&S, serviço não recorrente (teal). Categoria, não atenção: NÃO é warning,
                // e NÃO é laranja (o laranja é o acento da ação primária, §8.1). Decisão 04/10/2026.

/** Classes de badge (pill) por tom. */
export const TONE_BADGE: Record<StatusTone, string> = {
  open: 'bg-green-500/20 text-green-400',
  success: 'bg-emerald-500/20 text-emerald-400',
  progress: 'bg-blue-500/20 text-blue-300',
  live: 'bg-amber-500/20 text-amber-300 animate-pulse',
  warning: 'bg-amber-500/20 text-amber-400',
  danger: 'bg-red-500/20 text-red-400',
  neutral: 'bg-slate-500/20 text-slate-400',
  info: 'bg-purple-500/20 text-purple-300',
  group: 'bg-violet-500/20 text-violet-300',
  future: 'bg-sky-500/20 text-sky-300',
  regua: 'bg-cyan-500/20 text-cyan-300',
  ps: 'bg-teal-500/20 text-teal-300',
};

/** Cor de TEXTO/número por tom (stat cards estilo fiscal: a cor mora no número). */
export const TONE_TEXT: Record<StatusTone, string> = {
  open: 'text-green-400',
  success: 'text-emerald-400',
  progress: 'text-blue-400',
  live: 'text-amber-300',
  warning: 'text-amber-400',
  danger: 'text-red-400',
  neutral: 'text-slate-300',
  info: 'text-purple-400',
  group: 'text-violet-300',
  future: 'text-sky-300',
  regua: 'text-cyan-300',
  ps: 'text-teal-400',
};

/** Borda de destaque por tom (cards de atenção — padrão "NF Manual pendente"). */
export const TONE_BORDER: Record<StatusTone, string> = {
  open: 'border-green-600/60',
  success: 'border-emerald-600/60',
  progress: 'border-blue-600/60',
  live: 'border-amber-600/60',
  warning: 'border-amber-600/60',
  danger: 'border-red-600/60',
  neutral: 'border-slate-600',
  info: 'border-purple-600/60',
  group: 'border-violet-600/60',
  future: 'border-sky-600/60',
  regua: 'border-cyan-600/60',
  ps: 'border-teal-600/60',
};

/**
 * BARRA LATERAL de 3px por tom (12/10/2026, bloco E da revisão, molde da Central: estado é peso + ícone + barra).
 * Só os tons que pedem ação têm barra; o resto é transparente, e a borda fica na base da classe para o texto não
 * pular quando o estado muda.
 */
export const TONE_BARRA: Partial<Record<StatusTone, string>> = {
  warning: 'border-l-amber-500',
  danger: 'border-l-red-500',
};
export const SEM_BARRA = 'border-l-transparent';

/**
 * A BARRA PELO SINAL (23/10/2026, pedido do Heitor na #584): onde o número alterna de sinal de verdade (resultado do mês,
 * saldo do período, DRE), a barra é verde no positivo e vermelha no negativo. Não é a barra de estado: nos saldos que quase
 * sempre são positivos (o saldo de uma conta), verde em todos vira fundo e para de avisar; lá vale a barra só no negativo.
 */
export const BARRA_DO_SINAL = { positivo: 'border-l-emerald-500', negativo: 'border-l-red-500' } as const;

/**
 * Superfície TINTADA por tom (fundo suave, par natural do TONE_BORDER).
 *
 * Serve para blocos que agrupam por SIGNIFICADO — o diagrama de fluxos da
 * automação é o caso. NÃO serve para stat card: lá a regra do §2 continua
 * valendo, a cor mora no número e o fundo é neutro.
 */
export const TONE_SURFACE: Record<StatusTone, string> = {
  open: 'bg-green-500/10',
  success: 'bg-emerald-500/10',
  progress: 'bg-blue-500/10',
  live: 'bg-amber-500/10',
  warning: 'bg-amber-500/10',
  danger: 'bg-red-500/10',
  neutral: 'bg-slate-500/10',
  info: 'bg-purple-500/10',
  group: 'bg-violet-500/10',
  future: 'bg-sky-500/10',
  regua: 'bg-cyan-500/10',
  ps: 'bg-teal-500/10',
};

/** Superfícies padrão (§2 do guia). */
/**
 * A SUPERFÍCIE DA CENTRAL (S1, 24/10/2026). O `SURFACE` acima é o fundo antigo (slate) e ainda serve a dezenas de telas; cada
 * tela migrada passa a usar este, como a página de trabalho da proposta (L5). Os tons de estado (SURFACE.danger/warning) ficam.
 */
export const SURFACE_CENTRAL = {
  card: 'bg-central-cartao border border-central-borda rounded-central',
  inset: 'bg-central-elevado border border-central-borda rounded-lg',
} as const;

export const SURFACE = {
  card: 'bg-slate-800/50 border border-slate-700 rounded-xl',
  cardSelected: 'ring-1 ring-orange-500/60 bg-orange-500/5',
  input: 'bg-slate-900 border border-slate-700 rounded-lg focus:border-orange-500',
  tableHeader: 'bg-slate-800/80',
  panel: 'bg-slate-800/30 border border-slate-700 rounded-lg',
} as const;

/** Mapeamento status conhecido → tom. Cobre licitações, fiscal, propostas e docs.
 *  Casos novos: adicionar AQUI (nunca cor inline). */
const STATUS_TONE: Record<string, StatusTone> = {
  // licitações (procurement_items)
  analisando: 'warning', interessado: 'progress',
  participando: 'progress', aguardando_julgamento: 'warning',
  ganho: 'success', perdido: 'danger', homologado: 'neutral',
  adjudicado: 'neutral', encerrada: 'neutral', revogada: 'danger',
  anulada: 'danger', fracassada: 'danger', suspensa: 'warning',
  desistida: 'neutral', descartado: 'neutral',
  // disputa
  // A sessão grava 'ativo' no banco, mas a disputa não passa por aqui (DisputeSessionCard e
  // DisputeExpandedHeader têm selo próprio). Disputa ao vivo é `em_disputa`, logo abaixo.
  configurando: 'warning', aguardando: 'warning', finalizado: 'neutral', erro: 'danger',
  // funil de leads: o mesmo vocabulário da fatura (aberta / em andamento / paga /
  // cancelada), porque o funil é o mesmo tipo de trajetória. `novo` também é o status
  // de entrada do item de licitação, que a tabela de Licitações já mostra como
  // "Aberto" em verde. Ganho e Perdido já estão acima (success/danger).
  novo: 'open',
  contato: 'progress',
  poc: 'progress',
  proposta: 'progress',
  'negociação': 'progress',
  'geração_assinatura': 'progress',
  congelado: 'neutral',
  // propostas
  rascunho: 'warning', enviada: 'open', aceita: 'success', rejeitada: 'danger',
  aguardando_portal: 'warning',
  // fiscal
  autorizada: 'success', cancelada: 'neutral', pendente: 'warning', processando: 'progress',
  // documentos de habilitação
  vencido: 'danger', vencendo: 'warning', em_dia: 'open', sem_validade: 'neutral',
  // genéricos
  // `ativo` é o de assinatura/cliente (verde). Até 04/10/2026 era o `live` da disputa, e
  // badgeFor('Ativo') pulsava âmbar fora de Licitações.
  aberto: 'open', aberta: 'open', ativo: 'open', em_disputa: 'live',
  // grupo econômico (a mesma cor em lista, ficha, edição e painel)
  grupo: 'group', rateado: 'group', compartilhado: 'group', rateio: 'group',
  matriz: 'group', coligada: 'group',
  // vigência do item
  futuro: 'future', vigencia_futura: 'future',
  // serviços não recorrentes
  ps: 'ps', 'p&s': 'ps', servico: 'ps',
};

/** Badge pronto pra um status (string livre — normaliza e cai em neutral se desconhecido). */
export function badgeFor(status: string | null | undefined): string {
  const key = String(status || '').toLowerCase().trim().replace(/\s+/g, '_');
  return TONE_BADGE[STATUS_TONE[key] ?? 'neutral'];
}

/** Rótulos dos status de PROPOSTA (cor sempre via badgeFor — §4 do guia). */
export const PROPOSAL_STATUS_LABELS: Record<string, string> = {
  rascunho: 'Rascunho',
  aguardando_portal: 'Aguard. Portal',
  enviada: 'Enviada',
  aceita: 'Aceita',
  rejeitada: 'Rejeitada',
  cancelada: 'Cancelada',
  desistida: 'Desistida',
};

/**
 * O tom do status de PROPOSTA (L5, 24/10/2026, padrão da Central). Fica de fora do STATUS_TONE de propósito: lá "enviada" serve
 * também a faturas e e-mails. Aqui o que pede ação (rascunho, aguardando o portal) é âmbar, a vitória (aceita) se destaca, e o
 * resto (enviada, rejeitada, cancelada, desistida) é andamento ou fato consumado: neutro.
 */
const PROPOSAL_STATUS_TONE: Record<string, StatusTone> = {
  rascunho: 'warning',
  aguardando_portal: 'warning',
  enviada: 'neutral',
  aceita: 'success',
  rejeitada: 'neutral',
  cancelada: 'neutral',
  desistida: 'neutral',
};

/** Rótulo + classes de badge de um status de proposta. */
export function proposalStatusBadge(status: string | null | undefined): { label: string; color: string } {
  const key = String(status || 'rascunho').toLowerCase().trim();
  const tom = PROPOSAL_STATUS_TONE[key];
  return { label: PROPOSAL_STATUS_LABELS[key] || key, color: tom ? TONE_BADGE[tom] : badgeFor(key) };
}

/**
 * A BARRA DO GRUPO na lista de faturas (auditoria do painel, 07/10/2026): marca a linha do grupo e as filhas.
 *
 * ⛔ É sombra interna na PRIMEIRA CÉLULA, e não `border-l` na linha. A borda existia desde o começo e nunca
 * apareceu: o `tbody` usa `divide-y divide-slate-800`, cuja regra é mais específica que a classe de cor e
 * pinta as quatro bordas da linha de cinza (medido: rgb(30, 41, 59) na borda esquerda das filhas). A sombra
 * não participa da cascata de bordas, então nenhum divisor a apaga.
 */
export const BARRA_DE_GRUPO = {
  group: 'shadow-[inset_3px_0_0_rgb(139_92_246)]',
  info: 'shadow-[inset_3px_0_0_rgb(168_85_247)]',
} as const;

/**
 * A PÍLULA DO E-MAIL na coluna Documentos das Faturas (pedido do Heitor, 08/10/2026), por tom do sinal.
 * O e-mail é máquina de estados (não envia, enviado, entregue, aberto, parcial, falhou): estado é forma e peso.
 * - normal (enviado/entregue/aberto): neutra e peso NORMAL, porque são ~140 linhas por mês e não podem gritar;
 * - aviso e perigo: o tom do mapa, em SEMIBOLD. Medido nas capturas: com peso médio, a pílula vermelha ficava
 *   mais fraca que o texto semibold de antes, e a "nota com erro" abaixo chamava mais atenção que o e-mail;
 * - apagado ("não envia"): ausência correta, sem cor de estado, em itálico.
 * Mora aqui, e não na tela, porque é cor de status (guia §4, check-pilulas).
 */
export const PILULA_DO_EMAIL: Record<'quieto' | 'neutro' | 'apagado' | 'aviso' | 'perigo', string> = {
  quieto: 'font-normal bg-slate-500/15 text-slate-400',
  neutro: 'font-normal bg-slate-500/15 text-slate-400',
  apagado: 'font-normal bg-slate-500/10 text-slate-500 italic',
  aviso: `font-semibold ${TONE_BADGE.warning}`,
  perigo: `font-semibold ${TONE_BADGE.danger}`,
};

/** Fundo das FILHAS de um grupo: mais leve que o da linha do grupo (TONE_SURFACE), do mesmo matiz. */
export const FILHA_DE_GRUPO = {
  group: 'bg-violet-500/5',
  info: 'bg-purple-500/5',
} as const;

/**
 * A COR DA OPERADORA (22/10/2026, decisão do Heitor para a tela de DDRs). É IDENTIDADE, não estado: diz QUAL operadora é,
 * nunca se está bem ou mal. Por isso a paleta não usa vermelho, verde, âmbar, amarelo nem laranja (cores de estado e de
 * acento; o âmbar é o da DIVERGÊNCIA, que tem de continuar sendo o que mais chama). Tons discretos (fundo a 15%).
 *
 * Mora aqui, no mapa de cores, e não como pílula escrita na tela (check-pilulas). Operadora fora do mapa cai em NEUTRO, e
 * uma nova entra aqui. As chaves são o nome da prestadora (telecom_carriers.nome) em maiúsculas. Medido na base em
 * 22/10/2026: fornecedores TVN VOZ 3.887, ALGAR 1.954, SYSSVOIP 26, DATORA 1; operadoras reais TVN VOZ 3.605, ALGAR 1.954,
 * DATORA 283, TUBARON 26; a tabela tem também FALE SEMPRE.
 *
 * ⚠️ As três maiores usam matizes que nenhum tom acima usa (indigo, fúcsia, rosa). As outras três reaproveitam matizes de
 * tons (céu, violeta, ciano), porque não sobram matizes frios livres: a prévia levou isso ao Heitor.
 */
export const COR_DA_OPERADORA: Record<string, string> = {
  'TVN VOZ': 'bg-indigo-500/15 text-indigo-300',
  ALGAR: 'bg-fuchsia-500/15 text-fuchsia-300',
  DATORA: 'bg-pink-500/15 text-pink-300',
  SYSSVOIP: 'bg-sky-500/15 text-sky-300',
  TUBARON: 'bg-violet-500/15 text-violet-300',
  'FALE SEMPRE': 'bg-cyan-500/15 text-cyan-300',
};

/** A classe do chip da operadora; sem cor no mapa (ou sem nome), neutro. Caixa e espaços do nome não importam. */
export function corDaOperadora(nome?: string | null): string {
  return COR_DA_OPERADORA[String(nome || '').toUpperCase().trim()] ?? TONE_BADGE.neutral;
}
